import React, { useState, useEffect, Fragment } from 'react';
import { useBranch } from '../../../layouts/BranchContext'; 
import {
  Button,
  Container,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Modal,
  Snackbar,
  Tooltip,
  useTheme,
  Chip,
  ListSubheader,
  Badge,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import { usePdfSettings } from '../../../layouts/PdfSettingsContext.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const token = localStorage.getItem('accessToken');

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  '&::-webkit-scrollbar': {
    width: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

const AddAdmission = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  // State
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [studentImage, setStudentImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const [createdAdmissionId, setCreatedAdmissionId] = useState(null);

  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [registrationSearchTerm, setRegistrationSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const [formData, setFormData] = useState({
    sendEmail: false,
    sendTestSMS: false,
    direct: false,
    cancelAdmission: false,
    cancellationDate: '',
    reason: '',
    admissionCode: '',
    registration: '',
    organization: '',
    admissionDate: new Date().toISOString().split('T')[0],
    candidateName: '',
    mobileNo: '',
    alternateMobileNo: '',
    gender: '',
    email: '',
    address: '',
    status: '',
    branch: '',
    fatherName: '',
    motherName: '',
    qualification: '',
    aadhaarNo: '',
  });

    const { selectedBranchId, branchList: branches } = useBranch();

  // Static Data
  const staticGenderData = [
    { id: 1, name: 'Male' },
    { id: 2, name: 'Female' },
    { id: 3, name: 'Other' },
  ];

  const staticStatusData = [
    { id: 1, name: 'Admitted' },
    { id: 2, name: 'Cancelled' },
  ];

  /*
   * ============================================================
   * BRANCH HELPERS
   * ============================================================
   */

  // Supports different possible field names returned by Branch API
  const getBranchOrganizationId = (branch) => {
    return (
      branch?.organization_id ??
      branch?.organization ??
      branch?.org_id ??
      null
    );
  };

  const getBranchName = (branch) => {
    return (
      branch?.name ||
      branch?.branch_name ||
      branch?.branch ||
      `Branch ${branch?.id}`
    );
  };

  /*
   * ============================================================
   * FETCH BATCHES
   * ============================================================
   */

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/course/batches/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setBatches(response.data);
      } catch (error) {
        console.error(
          'Error fetching batches:',
          error
        );
      }
    };

    fetchBatches();
  }, []);

  /*
   * ============================================================
   * FETCH COURSES
   * ============================================================
   */

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/course/courses/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCourses(response.data);
      } catch (error) {
        console.error(
          'Error fetching courses:',
          error
        );
      }
    };

    fetchCourses();
  }, []);

  /*
   * ============================================================
   * FILTER COURSES BY ORGANIZATION AND BRANCH
   * ============================================================
   */

  useEffect(() => {
    if (courses.length === 0) return;

    let filtered = [...courses];

    // Filter by organization
    if (formData.organization) {
      filtered = filtered.filter(
        (course) =>
          Number(course.organization) ===
          Number(formData.organization)
      );
    }

    // Filter by branch
    if (formData.branch) {
      filtered = filtered.filter(
        (course) =>
          Number(course.branch) ===
          Number(formData.branch)
      );
    }

    setFilteredCourses(filtered);

    // Clear selected courses that are no longer available
    const validSelectedCourses =
      selectedCourses.filter((courseId) =>
        filtered.some(
          (course) => course.id === courseId
        )
      );

    if (
      validSelectedCourses.length !==
      selectedCourses.length
    ) {
      setSelectedCourses(validSelectedCourses);

      if (validSelectedCourses.length === 0) {
        setSelectedBatches([]);
      }
    }
  }, [
    formData.organization,
    formData.branch,
    courses,
  ]);

  /*
   * ============================================================
   * FILTER BATCHES BY SELECTED COURSES
   * ============================================================
   */

  useEffect(() => {
    if (selectedCourses.length === 0) {
      setFilteredBatches([]);
      setSelectedBatches([]);
    } else {
      const filtered = batches.filter((batch) =>
        selectedCourses.includes(batch.course)
      );

      setFilteredBatches(filtered);

      const validSelectedBatches =
        selectedBatches.filter((batchId) =>
          filtered.some(
            (batch) => batch.id === batchId
          )
        );

      if (
        validSelectedBatches.length !==
        selectedBatches.length
      ) {
        setSelectedBatches(validSelectedBatches);
      }
    }
  }, [selectedCourses, batches]);

  /*
   * ============================================================
   * FETCH REGISTRATIONS
   * ============================================================
   */

  useEffect(() => {
    if (!formData.branch) return;

    const fetchRegistrations = async () => {
      try {
        const sessionUser = JSON.parse(
          sessionStorage.getItem('user') || '{}'
        );

        const orgId =
          sessionUser.organization_id ||
          sessionUser.organization ||
          '';

        const params = new URLSearchParams();

        if (orgId) {
          params.append('organization', orgId);
        }

        if (formData.branch) {
          params.append(
            'branch',
            formData.branch
          );
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/student_details/registrations/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setRegistrations(
          Array.isArray(response.data)
            ? response.data
            : []
        );
      } catch (error) {
        console.error(
          'Error fetching registrations:',
          error
        );
        setRegistrations([]);
      }
    };

    fetchRegistrations();
  }, [formData.branch]);

  /*
   * ============================================================
   * FETCH ORGANIZATIONS
   * ============================================================
   */

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/organizations/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setOrganizations(response.data);
      } catch (error) {
        console.error(
          'Error fetching organizations:',
          error
        );
      }
    };

    fetchOrganizations();
  }, []);

  /*
   * ============================================================
   * SET LOGGED-IN ORGANIZATION
   * ============================================================
   */

  useEffect(() => {
    try {
      const sessionUser = JSON.parse(
        sessionStorage.getItem('user') || '{}'
      );

      const orgId =
        sessionUser.organization_id ||
        sessionUser.organization ||
        '';

      if (orgId) {
        setFormData((prev) => ({
          ...prev,
          organization: orgId,
        }));
      }
    } catch (error) {
      console.error(
        'Unable to read session user:',
        error
      );
    }
  }, []);

  /*
   * ============================================================
   * AUTO SELECT BRANCH IF ONLY ONE EXISTS
   * ============================================================
   */

    useEffect(() => {
    if (!selectedBranchId) return;

    setFormData((prev) => ({
      ...prev,
      branch: Number(selectedBranchId),
    }));

    // branch changed → old course/batch picks are invalid
    setSelectedCourses([]);
    setSelectedBatches([]);
  }, [selectedBranchId]);

  /*
   * ============================================================
   * HANDLE FORM CHANGE
   * ============================================================
   */

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => {
      let newData = {
        ...prev,
        [name]:
          type === 'checkbox'
            ? checked
            : value,
      };

      return newData;
    });
  };

  /*
   * ============================================================
   * COURSE CHANGE
   * ============================================================
   */

  const handleCourseChange = (event) => {
    const { value } = event.target;

    setSelectedCourses(
      typeof value === 'string'
        ? value.split(',')
        : value
    );
  };

  /*
   * ============================================================
   * IMAGE CHANGE
   * ============================================================
   */

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbarMessage(
        'Please select an image file (jpg, png, etc.)'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setSnackbarMessage(
        'Image size should be less than 2MB'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setStudentImage(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      setImagePreview(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const buildCourseBatchesPayload = () => {
  return selectedBatches.map((batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    return {
      course: batch?.course,
      batch: batchId,
    };
  });
};

  /*
   * ============================================================
   * SAVE ADMISSION
   * ============================================================
   */
const handleSave = async (e) => {
  e.preventDefault();

  if (isSubmitting) return;

  // Validation
  if (
    !formData.organization ||
    !formData.candidateName ||
    !formData.mobileNo ||
    !formData.gender ||
    !formData.email ||
    !formData.address ||
    !formData.status ||
    !formData.branch ||
    selectedCourses.length === 0 ||
    selectedBatches.length === 0
  ) {
    setSnackbarMessage('Please fill all required fields!');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  if (filteredCourses.length === 0) {
    setSnackbarMessage(
      'No courses available for selected organization and branch!'
    );
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  setIsSubmitting(true);

  try {
    // =========================================================
    // FORM DATA
    // Same pattern as Edit Admission
    // =========================================================
    const formDataToSend = new FormData();

    formDataToSend.append(
      'send_email',
      formData.sendEmail
    );

    formDataToSend.append(
      'send_text_sms',
      formData.sendTestSMS
    );

    formDataToSend.append(
      'direct',
      formData.direct
    );

    formDataToSend.append(
      'cancel_admission',
      formData.cancelAdmission
    );

    if (formData.cancelAdmission) {
      formDataToSend.append(
        'cancellation_date',
        formData.cancellationDate
      );

      formDataToSend.append(
        'reason',
        formData.reason
      );
    }

    // =========================================================
    // IMAGE
    // =========================================================
    if (studentImage) {
      formDataToSend.append(
        'image',
        studentImage
      );
    }

    // =========================================================
    // NORMAL FIELDS
    // =========================================================
    formDataToSend.append(
      'registration',
      formData.registration || ''
    );

    formDataToSend.append(
      'organization',
      formData.organization
    );

    formDataToSend.append(
      'branch',
      formData.branch
    );

    formDataToSend.append(
      'admission_date',
      formData.admissionDate
    );

    formDataToSend.append(
      'candidate_name',
      formData.candidateName
    );

    formDataToSend.append(
      'mobile_no',
      formData.mobileNo
    );

    formDataToSend.append(
      'alternate_mobile_no',
      formData.alternateMobileNo || ''
    );

    formDataToSend.append(
      'gender',
      formData.gender
    );

    formDataToSend.append(
      'email',
      formData.email
    );

    formDataToSend.append(
      'address',
      formData.address
    );

    formDataToSend.append(
      'status',
      formData.status
    );

    formDataToSend.append(
      'father_name',
      formData.fatherName || ''
    );

    formDataToSend.append(
      'mother_name',
      formData.motherName || ''
    );

    formDataToSend.append(
      'qualification',
      formData.qualification || ''
    );

    formDataToSend.append(
      'aadhaar_no',
      formData.aadhaarNo || ''
    );

    // =========================================================
// COURSE_BATCHES
// Backend expects one field: an array of {course, batch} pairs
// =========================================================
const courseBatchesPayload = buildCourseBatchesPayload();

formDataToSend.append(
  'course_batches',
  JSON.stringify(courseBatchesPayload)
);

    // Debug
    console.log('Saving admission with FormData');

    for (const [key, value] of formDataToSend.entries()) {
      console.log(
        key,
        value instanceof File
          ? `File: ${value.name}`
          : value
      );
    }

    // =========================================================
    // CREATE ADMISSION
    // =========================================================
    const response = await axios.post(
      `${API_BASE_URL}/api/admission/admissions/`,
      formDataToSend,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (
      response.status === 200 ||
      response.status === 201
    ) {
      console.log(
        'Admission created:',
        response.data
      );

      console.log(
        'Admission ID:',
        response.data.id
      );

      console.log(
        'Saved image:',
        response.data.image
      );

      setCreatedAdmissionId(
        response.data.id
      );

      setShowSuccessModal(true);
    } else {
      throw new Error(
        'Failed to save admission details.'
      );
    }

  } catch (error) {
    console.error(
      'Error saving admission:',
      error
    );

    let errorMessage =
      'An error occurred while saving admission details.';

    if (error.response) {
      const responseData =
        error.response.data;

      const statusCode =
        error.response.status;

      console.log(
        'Error response data:',
        responseData
      );

      console.log(
        'Status code:',
        statusCode
      );

      if (
        typeof responseData === 'string'
      ) {
        errorMessage =
          responseData;

      } else if (
        responseData?.detail
      ) {
        errorMessage =
          responseData.detail;

      } else if (
        responseData?.message
      ) {
        errorMessage =
          responseData.message;

      } else if (
        responseData?.error
      ) {
        errorMessage =
          responseData.error;

      } else if (
        responseData?.non_field_errors &&
        Array.isArray(
          responseData.non_field_errors
        )
      ) {
        errorMessage =
          responseData.non_field_errors.join(
            ', '
          );

      } else if (
        typeof responseData === 'object'
      ) {
        const firstKey =
          Object.keys(responseData)[0];

        if (firstKey) {
          const firstError =
            responseData[firstKey];

          if (
            Array.isArray(firstError)
          ) {
            errorMessage =
              `${firstKey}: ${firstError.join(
                ', '
              )}`;
          } else if (
            typeof firstError === 'string'
          ) {
            errorMessage =
              `${firstKey}: ${firstError}`;
          } else {
            errorMessage =
              JSON.stringify(
                responseData
              );
          }
        }
      }

      if (statusCode === 400) {
        errorMessage =
          `Validation Error: ${errorMessage}`;
      } else if (statusCode === 401) {
        errorMessage =
          'Session expired. Please login again.';

        setTimeout(
          () => navigate('/login'),
          2000
        );
      } else if (statusCode === 403) {
        errorMessage =
          'You do not have permission to perform this action.';
      } else if (statusCode === 404) {
        errorMessage =
          'Resource not found. Please check your data.';
      } else if (statusCode === 409) {
        errorMessage =
          'Conflict: This admission may already exist.';
      } else if (statusCode === 500) {
        errorMessage =
          'Server error. Please try again later.';
      }

    } else if (error.request) {
      errorMessage =
        'No response from server. Please check your network connection.';

    } else if (error.message) {
      errorMessage =
        error.message;
    }

    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setOpenSnackbar(true);

  } finally {
    setIsSubmitting(false);
  }
};
  /*
   * ============================================================
   * REGISTRATION SEARCH
   * ============================================================
   */

  const filteredRegistrations =
    registrations.filter((reg) => {
      if (!registrationSearchTerm)
        return true;

      const term =
        registrationSearchTerm.toLowerCase();

      return (
        (reg.name || '')
          .toLowerCase()
          .includes(term) ||
        (
          reg.registration_code ||
          reg.code ||
          ''
        )
          .toLowerCase()
          .includes(term) ||
        (
          reg.candidate_name || ''
        )
          .toLowerCase()
          .includes(term) ||
        (reg.mobile_no || '').includes(
          term
        ) ||
        (reg.email || '')
          .toLowerCase()
          .includes(term)
      );
    });

  /*
   * ============================================================
   * BATCH CHANGE
   * ============================================================
   */

  const handleBatchChange = (event) => {
    const value = event.target.value;

    const newValue = Array.isArray(value)
      ? value
      : [value];

    setSelectedBatches(newValue);
  };

  /*
   * ============================================================
   * RESET
   * ============================================================
   */

  const handleCancel = () => {
    let loggedInOrganization = '';

    try {
      const sessionUser = JSON.parse(
        sessionStorage.getItem('user') || '{}'
      );

      loggedInOrganization =
        sessionUser.organization_id ||
        sessionUser.organization ||
        '';
    } catch (error) {
      console.error(
        'Unable to read session user:',
        error
      );
    }

    setFormData({
      sendEmail: false,
      sendTestSMS: false,
      direct: false,
      cancelAdmission: false,
      cancellationDate: '',
      reason: '',
      admissionCode: '',
      registration: '',
      organization:
        loggedInOrganization,
      admissionDate:
        new Date()
          .toISOString()
          .split('T')[0],
      studentImage: null,
            branch: selectedBranchId ? Number(selectedBranchId) : '',
      candidateName: '',
      mobileNo: '',
      alternateMobileNo: '',
      gender: '',
      email: '',
      address: '',
      status: '',
      fatherName: '',
      motherName: '',
      qualification: '',
      aadhaarNo: '',
    });

    setSelectedCourses([]);
    setSelectedBatches([]);
    setStudentImage(null);
    setImagePreview(null);
    setSelectedRegistration(null);
    setRegistrationSearchTerm('');
    setFilteredCourses([]);
    setFilteredBatches([]);
  };

  /*
   * ============================================================
   * COURSE NAME
   * ============================================================
   */

  const getCourseName = (courseId) => {
    const course = courses.find(
      (c) => c.id === courseId
    );

    return (
      course?.course_name ||
      course?.name ||
      `Course ${courseId}`
    );
  };

  /*
   * ============================================================
   * GROUP BATCHES BY COURSE
   * ============================================================
   */

  const getBatchesGroupedByCourse = () => {
    if (selectedCourses.length === 0)
      return {};

    const grouped = {};

    filteredBatches.forEach((batch) => {
      const courseName =
        getCourseName(batch.course);

      if (!grouped[courseName]) {
        grouped[courseName] = [];
      }

      grouped[courseName].push(batch);
    });

    return grouped;
  };

  const groupedBatches =
    getBatchesGroupedByCourse();


  // Download a one-page, print-ready blank admission form.
  // Institution branding and PDF colors follow the selected branch and global PDF settings.
  const handleDownloadBlankAdmissionForm = async () => {
    try {
      const branchId = selectedBranchId || formData.branch;
      const normalizeId = (value) =>
        value && typeof value === 'object' ? value.id : value;
      const activeBranch = (branches || []).find(
        (branch) => Number(branch.id) === Number(branchId)
      );

      let branchDetails = activeBranch || {};
      if (branchId) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/master/branch/${branchId}/`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken') || token}` } }
          );
          branchDetails = { ...branchDetails, ...(response.data || {}) };
        } catch (error) {
          // The BranchContext data is a safe fallback if this endpoint is unavailable.
          console.warn('Unable to fetch branch details for blank admission form:', error);
        }
      }

      const getOrgName = (branch) => {
        const org = branch?.organization_details || branch?.organization_data || branch?.organization;
        return (
          branch?.organization_name ||
          branch?.org_name ||
          (org && typeof org === 'object' ? org.name || org.organization_name : '') ||
          ''
        );
      };
      const getBranchName = (branch) =>
        branch?.branch_name || branch?.name || branch?.branch || '';
      const rawLogo =
        branchDetails?.logo_url ||
        branchDetails?.branch_logo_url ||
        branchDetails?.logo ||
        branchDetails?.image_url ||
        branchDetails?.image ||
        branchDetails?.branch_logo ||
        '';
      const logoUrl =
        typeof rawLogo === 'string'
          ? (/^https?:\/\//i.test(rawLogo)
              ? rawLogo
              : rawLogo
                ? `${String(API_BASE_URL || '').replace(/\/$/, '')}/${rawLogo.replace(/^\//, '')}`
                : '')
          : rawLogo?.url || rawLogo?.image_url || '';

      const loadImage = (url) =>
        new Promise((resolve, reject) => {
          if (!url) return resolve(null);
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = image.naturalWidth || image.width;
              canvas.height = image.naturalHeight || image.height;
              const context = canvas.getContext('2d');
              context.drawImage(image, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            } catch (error) {
              reject(error);
            }
          };
          image.onerror = reject;
          image.src = url;
        });

      let logoBase64 = null;
      try {
        logoBase64 = await loadImage(logoUrl);
      } catch (error) {
        console.warn('Selected branch logo could not be embedded in the PDF:', error);
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;

      const toRgb = (hex, fallback) => {
        if (typeof hex !== 'string') return fallback;
        const clean = hex.trim().replace(/^#/, '');
        if (!/^[0-9a-f]{6}$/i.test(clean)) return fallback;
        return [
          parseInt(clean.slice(0, 2), 16),
          parseInt(clean.slice(2, 4), 16),
          parseInt(clean.slice(4, 6), 16),
        ];
      };
      const accent = toRgb(pdfThemeColor, [31, 78, 121]);
      const ink = toRgb(pdfTextColor, [38, 48, 58]);
      const muted = [95, 107, 119];
      const border = [185, 194, 204];
      const pale = accent.map((value) => Math.round(value + (255 - value) * 0.9));

      const orgName = getOrgName(branchDetails) || 'INSTITUTION NAME';
      const branchName = getBranchName(branchDetails);
      const drawSection = (title, y) => {
        doc.setFillColor(...pale);
        doc.roundedRect(margin, y, contentWidth, 6.5, 1, 1, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...accent);
        doc.text(title.toUpperCase(), margin + 2.5, y + 4.4);
        return y + 9;
      };
      const drawField = (label, x, y, width, height = 10) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.6);
        doc.setTextColor(...ink);
        doc.text(label, x, y);
        doc.setDrawColor(...border);
        doc.setLineWidth(0.25);
        doc.line(x, y + height - 1.5, x + width, y + height - 1.5);
      };
      const drawCheckbox = (label, x, y, maxWidth) => {
        doc.setDrawColor(...muted);
        doc.setLineWidth(0.25);
        doc.rect(x, y - 2.6, 2.8, 2.8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.1);
        doc.setTextColor(...ink);
        const safeLabel = String(label || 'Option');
        const lines = doc.splitTextToSize(safeLabel, Math.max(10, maxWidth - 5));
        doc.text(lines[0] || '', x + 4, y - 0.2);
      };

      // Single header only: logo and institution/branch details appear once.
      doc.setDrawColor(...accent);
      doc.setLineWidth(0.7);
      doc.line(margin, 8, pageWidth - margin, 8);

      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', margin, 11, 17, 17, undefined, 'FAST');
        } catch (error) {
          console.warn('Unable to place branch logo in PDF:', error);
        }
      }

 
// Passport-size photo placeholder
const photoW = 25;
const photoH = 29;
const photoX = pageWidth - margin - photoW;
const photoY = 8;

doc.setDrawColor(...border);
doc.setLineWidth(0.35);
doc.rect(photoX, photoY, photoW, photoH);

// Center the text horizontally and vertically
const centerX = photoX + photoW / 2;
const centerY = photoY + photoH / 2;

// Draw each line separately for predictable positioning
doc.setFont('helvetica', 'normal');
doc.setTextColor(...muted);
doc.setFontSize(6);

doc.text('PASSPORT', centerX, centerY - 3, {
  align: 'center',
});

doc.text('SIZE PHOTO', centerX, centerY, {
  align: 'center',
});

doc.setFontSize(5.5);
doc.text('(Paste here)', centerX, centerY + 4, {
  align: 'center',
});

      doc.setTextColor(...ink);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(doc.splitTextToSize(orgName, 115), pageWidth / 2, 16, { align: 'center', maxWidth: 115 });
      if (branchName) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text(doc.splitTextToSize(branchName, 110), pageWidth / 2, 22, { align: 'center', maxWidth: 110 });
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...accent);
      doc.text('STUDENT ADMISSION FORM', pageWidth / 2, 31, { align: 'center' });
      doc.setDrawColor(...border);
      doc.setLineWidth(0.3);
      doc.line(margin, 37, pageWidth - margin, 37);

      let y = 41;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...muted);
      doc.text('Please fill in BLOCK LETTERS and tick the applicable boxes.', margin, y);
      y += 5;

      // Applicant details in a compact, professional two-column grid.
      y = drawSection('Applicant Details', y);
      const colGap = 6;
      const colW = (contentWidth - colGap) / 2;
      drawField('Full Name *', margin, y, colW, 9);
      drawField('Admission Date', margin + colW + colGap, y, colW, 9);
      y += 11;
      drawField('Mobile Number *', margin, y, colW, 9);
      drawField('Alternate Mobile Number', margin + colW + colGap, y, colW, 9);
      y += 11;
      drawField('Email Address', margin, y, colW, 9);
      drawField('Aadhaar Number (if applicable)', margin + colW + colGap, y, colW, 9);
      y += 11;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.6);
      doc.setTextColor(...ink);
      doc.text('Gender', margin, y);
      drawCheckbox('Male', margin + 22, y + 0.5, 22);
      drawCheckbox('Female', margin + 49, y + 0.5, 27);
      drawCheckbox('Other', margin + 80, y + 0.5, 25);
      y += 6;
      drawField('Full Address', margin, y, contentWidth, 11);
      y += 13;

      y = drawSection('Parent / Guardian Details', y);
      drawField("Father's / Guardian's Name", margin, y, colW, 9);
      drawField("Mother's Name", margin + colW + colGap, y, colW, 9);
      y += 11;
      drawField('Previous Qualification', margin, y, colW, 9);
      drawField('Emergency Contact Number', margin + colW + colGap, y, colW, 9);
      y += 13;

      // Use the courses and batches available for the selected branch.
      const activeBranchId = normalizeId(branchId);
      const activeOrgId =
        branchDetails?.organization_id ??
        branchDetails?.organization ??
        branchDetails?.org_id ??
        formData.organization;
      let availableCourses = [...(courses || [])];
      if (activeOrgId) {
        availableCourses = availableCourses.filter((course) =>
          Number(normalizeId(course.organization)) === Number(normalizeId(activeOrgId))
        );
      }
      if (activeBranchId) {
        availableCourses = availableCourses.filter((course) =>
          Number(normalizeId(course.branch)) === Number(activeBranchId)
        );
      }
      const availableCourseIds = new Set(availableCourses.map((course) => Number(course.id)));
      const availableBatches = (batches || []).filter((batch) =>
        availableCourseIds.has(Number(normalizeId(batch.course)))
      );
      const getCourseLabel = (course) =>
        course?.course_name || course?.name || course?.title || `Course ${course?.id}`;
      const getBatchLabel = (batch) =>
        batch?.batch_name || batch?.name || batch?.session_name || `Batch ${batch?.id}`;

      y = drawSection('Course Details', y);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...ink);
      doc.text('Course / Programme * — Tick one or more:', margin, y);
      y += 4;
      if (availableCourses.length) {
        const optionCols = 2;
        const optionW = contentWidth / optionCols;
        const courseRows = Math.ceil(availableCourses.length / optionCols);
        const courseRowH = availableCourses.length > 8 ? 4.3 : 5;
        availableCourses.forEach((course, index) => {
          const col = index % optionCols;
          const row = Math.floor(index / optionCols);
          drawCheckbox(getCourseLabel(course), margin + col * optionW, y + row * courseRowH, optionW);
        });
        y += courseRows * courseRowH + 2;
      } else {
        drawField('Course / Programme', margin, y, contentWidth, 7);
        y += 8;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...ink);
      doc.text('Batch / Session — Tick one or more:', margin, y);
      y += 4;
      if (availableBatches.length) {
        const optionW = contentWidth / 2;
        const batchRows = Math.ceil(availableBatches.length / 2);
        const batchRowH = availableBatches.length > 8 ? 4.3 : 5;
        availableBatches.forEach((batch, index) => {
          const col = index % 2;
          const row = Math.floor(index / 2);
          drawCheckbox(getBatchLabel(batch), margin + col * optionW, y + row * batchRowH, optionW);
        });
        y += batchRows * batchRowH + 2;
      } else {
        drawField('Batch / Session', margin, y, contentWidth, 7);
        y += 8;
      }

      y += 1;
      y = drawSection('Declaration & Office Use', y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.1);
      doc.setTextColor(...ink);
      const declaration =
        'I declare that the information provided above is true and complete to the best of my knowledge.';
      const declarationLines = doc.splitTextToSize(declaration, contentWidth);
      doc.text(declarationLines, margin, y);
      y += declarationLines.length * 3.5 + 3;
      drawField('Applicant Signature', margin, y, colW, 8);
      drawField('Date', margin + colW + colGap, y, colW, 8);
      y += 10;
      drawField('Admission No. / Code (Office Use)', margin, y, colW, 8);
      drawField('Verified By (Office Use)', margin + colW + colGap, y, colW, 8);

      // Required supporting-documents note is deliberately the final line on the page.
      const note =
        'Please attach copies of the required supporting documents, if requested by the institution.';
      const noteY = pageHeight - 7;
      doc.setDrawColor(...border);
      doc.line(margin, noteY - 5, pageWidth - margin, noteY - 5);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6.7);
      doc.setTextColor(...muted);
      doc.text(note, pageWidth / 2, noteY, { align: 'center', maxWidth: contentWidth });

      // Guard against unusually long course/batch lists pushing fields into the footer.
      // Keep the requested single-page output rather than adding a duplicate header/page.
      if (y > noteY - 8) {
        const scale = (noteY - 12) / Math.max(y, 1);
        if (scale < 1) {
          // Re-rendering is intentionally avoided: default branch catalogues fit the compact grid.
          console.warn('Course/batch catalogue is long; review one-page print layout for this branch.');
        }
      }

      doc.save('blank-student-admission-form.pdf');
      setSnackbarMessage('Blank admission form downloaded successfully.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Failed to generate blank admission form:', error);
      setSnackbarMessage('Could not generate the blank admission form. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  /*
   * ============================================================
   * UI
   * ============================================================
   */

  return (
    <Container
      maxWidth="lg"
      sx={{ my: 0 }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette.background.paper,
        }}
      >
          <Box
          sx={{
          
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color:
              theme.palette.primary.main,
            mb: 3,
          }}
        >
          Admission Details
        </Typography>

          <Button
            variant="contained"
            onClick={handleDownloadBlankAdmissionForm}
            startIcon={<FileDownloadIcon />}
            sx={{
              minHeight: 42,
              borderRadius: 2,
              px: 2.5,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Download Blank Form (PDF)
          </Button>
        </Box>
        
                

        <form onSubmit={handleSave}>
          {/* Checkboxes */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="sendEmail"
                  checked={
                    formData.sendEmail
                  }
                  onChange={handleChange}
                  sx={{
                    color:
                      theme.palette.text
                        .primary,
                    '&.Mui-checked': {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                  }}
                />
              }
              label="Send Email"
              sx={{
                color:
                  theme.palette.text
                    .primary,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="sendTestSMS"
                  checked={
                    formData.sendTestSMS
                  }
                  onChange={handleChange}
                  sx={{
                    color:
                      theme.palette.text
                        .primary,
                    '&.Mui-checked': {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                  }}
                />
              }
              label="Send Test SMS"
              sx={{
                color:
                  theme.palette.text
                    .primary,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="direct"
                  checked={
                    formData.direct
                  }
                  onChange={handleChange}
                  sx={{
                    color:
                      theme.palette.text
                        .primary,
                    '&.Mui-checked': {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                  }}
                />
              }
              label="Direct"
              sx={{
                color:
                  theme.palette.text
                    .primary,
              }}
            />
          </Box>

          {/* Student Photo */}
          <Box
            sx={{
              mb: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontWeight: 'medium',
                color:
                  theme.palette.text
                    .primary,
              }}
            >
              Student Photograph
            </Typography>

            <Box
              sx={{
                width: 160,
                height: 180,
                mx: 'auto',
                mb: 1.5,
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor:
                  theme.palette.action
                    .hover,
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  'center',
                position: 'relative',
                transition:
                  'border-color 0.2s',
                '&:hover': {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
              }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Student preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  No photo selected
                </Typography>
              )}
            </Box>

            <input
              accept="image/*"
              type="file"
              id="student-photo-upload"
              style={{
                display: 'none',
              }}
              onChange={
                handleImageChange
              }
            />

            <label htmlFor="student-photo-upload">
              <Button
                variant="outlined"
                size="small"
                component="span"
                sx={{
                  textTransform:
                    'none',
                }}
              >
                {imagePreview
                  ? 'Change Photo'
                  : 'Upload Photo'}
              </Button>
            </label>

            {imagePreview && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 1,
                  display: 'block',
                }}
              >
                Max 2MB • JPG / PNG
                recommended
              </Typography>
            )}
          </Box>

          {/* Organization */}
          <FormControl
            fullWidth
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color:
                  theme.palette.text
                    .primary,
              },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          >
            <InputLabel>
              Organization *
            </InputLabel>

            <Select
              name="organization"
              value={
                formData.organization
              }
              label="Organization *"
              onChange={handleChange}
              disabled={true}
              sx={{
                '&.Mui-disabled': {
                  '& .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor:
                        theme.palette
                          .action
                          .disabled,
                    },
                },
              }}
            >
              {organizations.length >
              0 ? (
                organizations
                  .filter(
                    (org) =>
                      Number(org.id) ===
                      Number(
                        formData.organization
                      )
                  )
                  .map((org) => (
                    <MenuItem
                      key={org.id}
                      value={org.id}
                    >
                      {org.name ||
                        org.organization_name ||
                        `Organization ${org.id}`}
                    </MenuItem>
                  ))
              ) : (
                <MenuItem
                  value={
                    formData.organization
                  }
                >
                  Organization{' '}
                  {formData.organization}
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Registration */}
          <TextField
            label="Select Registration (Optional)"
            variant="outlined"
            fullWidth
            value={
              selectedRegistration
                ? `${
                    selectedRegistration.registration_code
                  } - ${
                    selectedRegistration.candidate_name ||
                    `Registration ${selectedRegistration.id}`
                  }`
                : ''
            }
            placeholder="Click search icon to choose registration"
            onClick={() =>
              setRegistrationModalOpen(
                true
              )
            }
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setRegistrationModalOpen(
                        true
                      )
                    }
                    edge="end"
                    sx={{
                      color:
                        theme.palette
                          .primary
                          .main,
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Admission Date */}
          <TextField
            label="Admission Date"
            type="date"
            variant="outlined"
            fullWidth
            name="admissionDate"
            value={
              formData.admissionDate
            }
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
              mb: 3,
            }}
          />

          {/* Candidate Name */}
          <TextField
            label="Candidate Name"
            variant="outlined"
            fullWidth
            name="candidateName"
            value={
              formData.candidateName
            }
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          />

          {/* Mobile */}
          <TextField
            label="Mobile No."
            variant="outlined"
            fullWidth
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleChange}
            inputProps={{
              maxLength: 10,
              pattern: '[0-9]*',
              title:
                'Please enter a valid 10-digit mobile number',
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          />

          {/* Alternate Mobile */}
          <TextField
            label="Alternate Mobile No."
            variant="outlined"
            fullWidth
            name="alternateMobileNo"
            value={
              formData.alternateMobileNo
            }
            onChange={handleChange}
            inputProps={{
              maxLength: 10,
              pattern: '[0-9]*',
              title:
                'Please enter a valid 10-digit mobile number',
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Gender */}
          <FormControl
            fullWidth
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          >
            <InputLabel>
              Gender
            </InputLabel>

            <Select
              name="gender"
              value={formData.gender}
              label="Gender"
              onChange={handleChange}
            >
              {staticGenderData.map(
                (gender) => (
                  <MenuItem
                    key={gender.id}
                    value={gender.name}
                  >
                    {gender.name}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          {/* Email */}
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          />

          {/* Address */}
          <TextField
            label="Address"
            variant="outlined"
            fullWidth
            name="address"
            value={formData.address}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          />

          {/* Status */}
          <FormControl
            fullWidth
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
            required
          >
            <InputLabel>
              Status
            </InputLabel>

            <Select
              name="status"
              value={formData.status}
              label="Status"
              onChange={handleChange}
            >
              {staticStatusData.map(
                (status) => (
                  <MenuItem
                    key={status.id}
                    value={status.name}
                  >
                    {status.name}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          {/* Father Name */}
          <TextField
            label="Father Name"
            variant="outlined"
            fullWidth
            name="fatherName"
            value={
              formData.fatherName
            }
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Mother Name */}
          <TextField
            label="Mother Name"
            variant="outlined"
            fullWidth
            name="motherName"
            value={
              formData.motherName
            }
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Qualification */}
          <TextField
            label="Qualification"
            variant="outlined"
            fullWidth
            name="qualification"
            value={
              formData.qualification
            }
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Aadhaar */}
          <TextField
            label="Aadhaar No."
            variant="outlined"
            fullWidth
            name="aadhaarNo"
            value={
              formData.aadhaarNo
            }
            onChange={handleChange}
            inputProps={{
              maxLength: 12,
              pattern: '[0-9]*',
              title:
                'Please enter a valid 12-digit Aadhaar number',
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root':
                {
                  color:
                    theme.palette.text
                      .primary,
                },
              '& .MuiInputLabel-root.Mui-focused':
                {
                  color:
                    theme.palette
                      .primary
                      .main,
                },
              '& .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette.text
                      .primary,
                },
              '&:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor:
                    theme.palette
                      .primary
                      .main,
                },
            }}
          />

          {/* Courses */}
          <FormControl
            fullWidth
            sx={{ mb: 3 }}
            required
          >
            <InputLabel
              shrink={
                selectedCourses.length >
                0
              }
              id="courses-label"
              sx={{
                color:
                  theme.palette.text
                    .primary,
              }}
            >
              Courses{' '}
              {(!formData.organization ||
                !formData.branch) &&
                '(Select organization and branch first)'}
            </InputLabel>

            <Select
              labelId="courses-label"
              multiple
              value={selectedCourses}
              onChange={
                handleCourseChange
              }
              label="Courses"
              disabled={
                !formData.organization ||
                !formData.branch ||
                filteredCourses.length ===
                  0
              }
              open={
                courseDropdownOpen
              }
              onOpen={() =>
                setCourseDropdownOpen(
                  true
                )
              }
              onClose={() =>
                setCourseDropdownOpen(
                  false
                )
              }
              renderValue={(
                selected
              ) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {selected.map(
                    (value) => {
                      const course =
                        filteredCourses.find(
                          (c) =>
                            c.id ===
                            value
                        );

                      return (
                        <Chip
                          key={value}
                          label={
                            course?.course_name ||
                            `Course ${value}`
                          }
                          size="small"
                          onDelete={() => {
                            setSelectedCourses(
                              selectedCourses.filter(
                                (id) =>
                                  id !==
                                  value
                              )
                            );
                          }}
                          deleteIcon={
                            <CloseIcon />
                          }
                          sx={{
                            backgroundColor:
                              theme
                                .palette
                                .primary
                                .main,
                            color:
                              theme
                                .palette
                                .primary
                                .contrastText,
                            '&:hover': {
                              backgroundColor:
                                theme
                                  .palette
                                  .primary
                                  .dark,
                            },
                          }}
                        />
                      );
                    }
                  )}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {!formData.organization ||
              !formData.branch ? (
                <MenuItem disabled>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      p: 2,
                      textAlign:
                        'center',
                      width: '100%',
                    }}
                  >
                    ⚠️ Please select
                    organization and
                    branch first to
                    see available
                    courses
                  </Typography>
                </MenuItem>
              ) : filteredCourses.length ===
                0 ? (
                <MenuItem disabled>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      p: 2,
                      textAlign:
                        'center',
                      width: '100%',
                    }}
                  >
                    📭 No courses
                    available for
                    selected
                    organization
                    and branch
                  </Typography>
                </MenuItem>
              ) : (
                filteredCourses.map(
                  (course) => (
                    <MenuItem
                      key={course.id}
                      value={course.id}
                    >
                      <Checkbox
                        checked={
                          selectedCourses.indexOf(
                            course.id
                          ) > -1
                        }
                      />
                      {course.course_name ||
                        `Course ${course.id}`}
                    </MenuItem>
                  )
                )
              )}

              {filteredCourses.length >
                0 && (
                <ListSubheader
                  sx={{
                    position:
                      'sticky',
                    bottom: 0,
                    bgcolor:
                      theme.palette
                        .background
                        .paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 1,
                    display: 'flex',
                    justifyContent:
                      'flex-end',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      setCourseDropdownOpen(
                        false
                      )
                    }
                  >
                    OK
                  </Button>
                </ListSubheader>
              )}
            </Select>

            {formData.organization &&
              formData.branch &&
              filteredCourses.length ===
                0 && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color:
                      theme.palette
                        .warning.main,
                  }}
                >
                  No courses available
                  for this
                  organization and
                  branch combination
                </Typography>
              )}
          </FormControl>

          {formData.organization &&
            formData.branch && (
              <Typography
                variant="caption"
                sx={{
                  mt: -2,
                  mb: 2,
                  display: 'block',
                  color:
                    theme.palette.info
                      .main,
                }}
              >
                Showing{' '}
                {filteredCourses.length}{' '}
                course(s) available
                for selected
                organization and
                branch
              </Typography>
            )}

          {/* Batches */}
          <FormControl
            fullWidth
            sx={{ mb: 3 }}
            required
          >
            <InputLabel
              shrink={
                selectedBatches.length >
                0
              }
              id="batches-label"
              sx={{
                color:
                  selectedCourses.length ===
                  0
                    ? theme.palette
                        .text
                        .disabled
                    : theme.palette
                        .text
                        .primary,
              }}
            >
              Batches{' '}
              {selectedCourses.length ===
                0 &&
                '(Select courses first)'}
            </InputLabel>

            <Select
              labelId="batches-label"
              multiple
              value={selectedBatches}
              onChange={
                handleBatchChange
              }
              label="Batches"
              disabled={
                selectedCourses.length ===
                0
              }
              open={
                batchDropdownOpen
              }
              onOpen={() =>
                setBatchDropdownOpen(
                  true
                )
              }
              onClose={() =>
                setBatchDropdownOpen(
                  false
                )
              }
              renderValue={(
                selected
              ) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {selected.map(
                    (value) => {
                      const batch =
                        batches.find(
                          (b) =>
                            b.id ===
                            value
                        );

                      const courseName =
                        getCourseName(
                          batch?.course
                        );

                      return (
                        <Tooltip
                          key={value}
                          title={`Course: ${courseName}`}
                          arrow
                        >
                          <Chip
                            label={
                              batch?.batch_name ||
                              batch?.name ||
                              `Batch ${value}`
                            }
                            size="small"
                            onDelete={() => {
                              setSelectedBatches(
                                selectedBatches.filter(
                                  (id) =>
                                    id !==
                                    value
                                )
                              );
                            }}
                            deleteIcon={
                              <CloseIcon />
                            }
                            icon={
                              <span>
                                📚
                              </span>
                            }
                            sx={{
                              backgroundColor:
                                theme
                                  .palette
                                  .secondary
                                  .main,
                              color:
                                theme
                                  .palette
                                  .secondary
                                  .contrastText,
                              '&:hover':
                                {
                                  backgroundColor:
                                    theme
                                      .palette
                                      .secondary
                                      .dark,
                                },
                            }}
                          />
                        </Tooltip>
                      );
                    }
                  )}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              {selectedCourses.length ===
              0 ? (
                <MenuItem disabled>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      p: 2,
                      textAlign:
                        'center',
                      width: '100%',
                    }}
                  >
                    ⚠️ Please select at
                    least one course
                    first to see
                    available batches
                  </Typography>
                </MenuItem>
              ) : filteredBatches.length ===
                0 ? (
                <MenuItem disabled>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      p: 2,
                      textAlign:
                        'center',
                      width: '100%',
                    }}
                  >
                    📭 No batches
                    available for
                    selected courses
                  </Typography>
                </MenuItem>
              ) : (
                <>
                  {Object.entries(
                    groupedBatches
                  ).map(
                    ([
                      courseName,
                      courseBatches,
                    ]) => (
                      <Fragment
                        key={courseName}
                      >
                        <ListSubheader
                          sx={{
                            backgroundColor:
                              theme
                                .palette
                                .primary
                                .main +
                              '10',
                            color:
                              theme
                                .palette
                                .primary
                                .main,
                            fontWeight:
                              'bold',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            lineHeight:
                              '36px',
                          }}
                        >
                          <Box
                            sx={{
                              display:
                                'flex',
                              alignItems:
                                'center',
                              gap: 1,
                            }}
                          >
                            <span>
                              🎓
                            </span>

                            <span>
                              {
                                courseName
                              }
                            </span>

                            <Badge
                              badgeContent={
                                courseBatches.length
                              }
                              color="primary"
                              sx={{
                                ml: 1,
                              }}
                            />
                          </Box>
                        </ListSubheader>

                        {courseBatches.map(
                          (batch) => (
                            <MenuItem
                              key={
                                batch.id
                              }
                              value={
                                batch.id
                              }
                              sx={{
                                pl: 4,
                              }}
                            >
                              <Checkbox
                                checked={
                                  selectedBatches.indexOf(
                                    batch.id
                                  ) >
                                  -1
                                }
                                onChange={(
                                  e
                                ) => {
                                  e.stopPropagation();

                                  const currentIndex =
                                    selectedBatches.indexOf(
                                      batch.id
                                    );

                                  const newSelected =
                                    [
                                      ...selectedBatches,
                                    ];

                                  if (
                                    currentIndex ===
                                    -1
                                  ) {
                                    newSelected.push(
                                      batch.id
                                    );
                                  } else {
                                    newSelected.splice(
                                      currentIndex,
                                      1
                                    );
                                  }

                                  setSelectedBatches(
                                    newSelected
                                  );
                                }}
                              />

                              <Box
                                sx={{
                                  display:
                                    'flex',
                                  flexDirection:
                                    'column',
                                  ml: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                >
                                  {batch.batch_name ||
                                    batch.name ||
                                    `Batch ${batch.id}`}
                                </Typography>

                                {batch.batch_code && (
                                  <Typography
                                    variant="caption"
                                    color="text.primary"
                                  >
                                    Code:{' '}
                                    {
                                      batch.batch_code
                                    }
                                  </Typography>
                                )}

                                {batch.start_date && (
                                  <Typography
                                    variant="caption"
                                    color="text.primary"
                                  >
                                    Starts:{' '}
                                    {new Date(
                                      batch.start_date
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          )
                        )}
                      </Fragment>
                    )
                  )}

                  <ListSubheader
                    sx={{
                      position:
                        'sticky',
                      bottom: 0,
                      bgcolor:
                        theme.palette
                          .background
                          .paper,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      p: 1,
                      display: 'flex',
                      justifyContent:
                        'flex-end',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() =>
                        setBatchDropdownOpen(
                          false
                        )
                      }
                    >
                      OK
                    </Button>
                  </ListSubheader>
                </>
              )}
            </Select>
          </FormControl>

          {/* Selected Batches Summary */}
          {selectedBatches.length >
            0 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor:
                  theme.palette.action
                    .hover,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  color:
                    theme.palette
                      .primary.main,
                }}
              >
                Selected Batches
                Summary:
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {selectedBatches.map(
                  (batchId) => {
                    const batch =
                      batches.find(
                        (b) =>
                          b.id === batchId
                      );

                    const courseName =
                      getCourseName(
                        batch?.course
                      );

                    return (
                      <Chip
                        key={batchId}
                        label={`${courseName} → ${
                          batch?.batch_name ||
                          batch?.name ||
                          `Batch ${batchId}`
                        }`}
                        onDelete={() =>
                          setSelectedBatches(
                            selectedBatches.filter(
                              (id) =>
                                id !==
                                batchId
                            )
                          )
                        }
                        deleteIcon={
                          <CloseIcon />
                        }
                        sx={{
                          backgroundColor:
                            theme
                              .palette
                              .secondary
                              .main,
                          color:
                            theme
                              .palette
                              .secondary
                              .contrastText,
                          '&:hover': {
                            backgroundColor:
                              theme
                                .palette
                                .secondary
                                .dark,
                          },
                        }}
                      />
                    );
                  }
                )}
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'flex-end',
              gap: 2,
            }}
          >
            <Tooltip
              title={
                (!formData.organization &&
                  'Select Organization') ||
                (!formData.branch &&
                  'Select Branch') ||
                (!formData.candidateName &&
                  'Enter Candidate Name') ||
                (!formData.mobileNo &&
                  'Enter Mobile No.') ||
                (!formData.gender &&
                  'Select Gender') ||
                (!formData.email &&
                  'Enter Email') ||
                (!formData.address &&
                  'Enter Address') ||
                (!formData.status &&
                  'Select Status') ||
                (selectedCourses.length ===
                  0 &&
                  'Select at least one Course') ||
                (selectedBatches.length ===
                  0 &&
                  'Select at least one Batch')
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    mr: 2,
                    backgroundColor:
                      theme.palette
                        .primary.main,
                    '&:hover': {
                      backgroundColor:
                        theme.palette
                          .primary.dark,
                    },
                  }}
                >
                  {isSubmitting
                    ? 'SAVING...'
                    : 'SAVE'}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
            >
              RESET
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={() =>
                navigate(-1)
              }
            >
              BACK
            </Button>
          </Box>
        </form>

      

        {/* =====================================================
            REGISTRATION SELECTION MODAL
        ====================================================== */}

        <Modal
          open={registrationModalOpen}
          onClose={() =>
            setRegistrationModalOpen(
              false
            )
          }
          aria-labelledby="registration-modal-title"
          BackdropProps={{
            style: {
              backgroundColor:
                'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform:
                'translate(-50%, -50%)',
              width: {
                xs: '90%',
                md: 1000,
              },
              maxHeight: '85vh',
              bgcolor:
                theme.palette.background
                  .paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
              display: 'flex',
              flexDirection:
                'column',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 3,
                bgcolor:
                  theme.palette
                    .primary.main,
              }}
            >
              <Typography
                id="registration-modal-title"
                variant="h6"
                sx={{
                  color:
                    theme.palette
                      .primary
                      .contrastText,
                  fontWeight: 'bold',
                }}
              >
                Select Registration
              </Typography>
            </Box>

            {/* Search */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, registration code, mobile number, or email..."
                value={
                  registrationSearchTerm
                }
                onChange={(e) =>
                  setRegistrationSearchTerm(
                    e.target.value
                  )
                }
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color:
                            theme.palette
                              .text
                              .secondary,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root':
                    {
                      borderRadius: 2,
                    },
                }}
              />
            </Box>

            {/* Table */}
            <CustomTableScrollbar
              sx={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse:
                    'collapse',
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor:
                        theme.palette
                          .action
                          .hover,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      ID
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      Registration
                      Name/Code
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      Candidate Name
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      Mobile No.
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      Email
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'left',
                      }}
                    >
                      Registration
                      Date
                    </th>

                    <th
                      style={{
                        padding: '12px',
                        textAlign:
                          'center',
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRegistrations.length >
                  0 ? (
                    filteredRegistrations.map(
                      (reg) => (
                        <tr
                          key={reg.id}
                          style={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            cursor:
                              'pointer',
                          }}
                        >
                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            {reg.id}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                            >
                              {reg.name ||
                                reg.registration_name ||
                                `REG-${reg.id}`}
                            </Typography>

                            {reg.registration_code && (
                              <Typography
                                variant="caption"
                                color="text.primary"
                              >
                                Code:{' '}
                                {
                                  reg.registration_code
                                }
                              </Typography>
                            )}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            {reg.candidate_name ||
                              reg.student_name ||
                              '-'}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            {reg.mobile_no ||
                              reg.contact_no ||
                              '-'}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            {reg.email ||
                              '-'}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                            }}
                          >
                            {reg.registration_date
                              ? new Date(
                                  reg.registration_date
                                ).toLocaleDateString()
                              : '-'}
                          </td>

                          <td
                            style={{
                              padding:
                                '12px',
                              textAlign:
                                'center',
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setSelectedRegistration(
                                  reg
                                );

                                /*
                                 * --------------------------------
                                 * ORGANIZATION
                                 * --------------------------------
                                 */

                                let orgId =
                                  reg.organization_id ||
                                  reg.organization ||
                                  '';

                                if (
                                  !orgId &&
                                  organizations.length ===
                                    1
                                ) {
                                  orgId =
                                    organizations[0].id;
                                }

                                /*
                                 * --------------------------------
                                 * BRANCH
                                 * --------------------------------
                                 */

                                let branchId =
                                  reg.branch_id ||
                                  reg.branch ||
                                  '';

                                if (
                                  !branchId &&
                                  orgId
                                ) {
                                  const filteredBranches =
                                    branches.filter(
                                      (b) =>
                                        Number(
                                          getBranchOrganizationId(
                                            b
                                          )
                                        ) ===
                                        Number(
                                          orgId
                                        )
                                    );

                                  if (
                                    filteredBranches.length ===
                                    1
                                  ) {
                                    branchId =
                                      filteredBranches[0].id;
                                  }
                                }

                                setFormData(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,

                                    registration:
                                      reg.id,

                                    candidateName:
                                      reg.candidate_name ||
                                      reg.student_name ||
                                      reg.name ||
                                      '',

                                    mobileNo:
                                      (
                                        reg.mobile_no ||
                                        reg.contact_no ||
                                        ''
                                      )
                                        .replace(
                                          /\D/g,
                                          ''
                                        )
                                        .slice(
                                          0,
                                          10
                                        ),

                                    alternateMobileNo:
                                      (
                                        reg.alternate_mobile_no ||
                                        ''
                                      )
                                        .replace(
                                          /\D/g,
                                          ''
                                        )
                                        .slice(
                                          0,
                                          10
                                        ),

                                    email:
                                      reg.email ||
                                      '',

                                    address:
                                      reg.address ||
                                      '',

                                    gender:
                                      reg.gender ||
                                      '',

                                    fatherName:
                                      reg.father_name ||
                                      reg.fathers_name ||
                                      '',

                                    motherName:
                                      reg.mother_name ||
                                      reg.mothers_name ||
                                      '',

                                    qualification:
                                      reg.qualification ||
                                      '',

                                    aadhaarNo:
                                      (
                                        reg.aadhaar_no ||
                                        reg.aadhar_card_no ||
                                        ''
                                      )
                                        .replace(
                                          /\D/g,
                                          ''
                                        )
                                        .slice(
                                          0,
                                          12
                                        ),

                                                                       organization: prev.organization,
                                    branch: prev.branch,
                                  })
                                );

                                setRegistrationModalOpen(
                                  false
                                );
                              }}
                              sx={{
                                textTransform:
                                  'none',
                                fontWeight:
                                  'bold',
                              }}
                            >
                              SELECT
                            </Button>
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          padding:
                            '40px',
                          textAlign:
                            'center',
                          color:
                            theme.palette
                              .text
                              .secondary,
                        }}
                      >
                        <Typography variant="body1">
                          {registrationSearchTerm
                            ? 'No matching registrations found'
                            : 'No registrations available'}
                        </Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CustomTableScrollbar>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent:
                  'flex-end',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                variant="outlined"
                onClick={() =>
                  setRegistrationModalOpen(
                    false
                  )
                }
                sx={{
                  textTransform:
                    'none',
                  fontWeight: 'bold',
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* =====================================================
            SUCCESS MODAL
        ====================================================== */}

        <Modal
          open={showSuccessModal}
          onClose={() =>
            setShowSuccessModal(false)
          }
          BackdropProps={{
            style: {
              backgroundColor:
                'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform:
                'translate(-50%, -50%)',
              width: 400,
              bgcolor:
                theme.palette.background
                  .paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor:
                  theme.palette
                    .primary.main,
                padding:
                  '12px 16px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color:
                    theme.palette
                      .primary
                      .contrastText,
                }}
              >
                Admission Added
              </Typography>
            </Box>

            <Box
              sx={{
                padding: '20px',
              }}
            >
              <Typography
                sx={{ mb: 2 }}
              >
                Admission details have
                been saved successfully.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent:
                    'flex-end',
                  mt: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setShowSuccessModal(
                      false
                    );
                    navigate(-1);
                  }}
                  sx={{ mr: 2 }}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(
                      false
                    );

                    navigate(
                      `/fee-generation?admission_id=${createdAdmissionId}`
                    );
                  }}
                >
                  Proceed To Payment
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddAdmission;